/**
 * Sound cues and haptics (Sprint 2, S2-14 / S2-17). All sounds are synthesised with Web Audio — no files to load and
 * no licences to track (deviation from plan D7, which assumed CC0 samples). Off by default; the AudioContext is
 * created on the first cue after the user switched sound on (that switch is the user gesture browsers require).
 *
 * Cues: `click` (UI press), `detent` (throttle notch), `swell` (a flyby begins), `ping` (closest approach),
 * `rumble` (intro). Haptics: a short pattern on closest approach, Android only, never with reduced motion (D8).
 */
import { get } from 'svelte/store';
import { motionReduced, soundOn } from '../state/settings';

export type Cue = 'click' | 'detent' | 'swell' | 'ping' | 'rumble';

const MASTER = 0.35;

let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let noise: AudioBuffer | null = null;

function audio(): { ctx: AudioContext; out: GainNode } | null {
  if (!get(soundOn)) return null;
  const AC = globalThis.AudioContext as typeof AudioContext | undefined;
  if (!AC) return null;
  if (!ctx) {
    try {
      ctx = new AC();
      master = ctx.createGain();
      master.gain.value = MASTER;
      master.connect(ctx.destination);
    } catch {
      ctx = null;
      return null;
    }
  }
  if (ctx.state === 'suspended') void ctx.resume();
  return { ctx, out: master! };
}

/** One second of white noise, generated once. */
function noiseBuffer(c: AudioContext): AudioBuffer {
  if (noise) return noise;
  noise = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = noise.getChannelData(0);
  let seed = 1977;
  for (let i = 0; i < data.length; i++) {
    seed = (seed * 16807) % 2147483647;
    data[i] = (seed / 2147483647) * 2 - 1;
  }
  return noise;
}

/** Attack/decay envelope on a gain node. */
function envelope(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

/** Plays a cue if sound is on; silently does nothing otherwise (or without Web Audio). */
export function cue(name: Cue): void {
  const a = audio();
  if (!a) return;
  const { ctx: c, out } = a;
  const t = c.currentTime + 0.005;
  const g = c.createGain();
  g.connect(out);
  switch (name) {
    case 'click': {
      // a dry mechanical tick: band-passed noise, 25 ms
      const src = c.createBufferSource();
      src.buffer = noiseBuffer(c);
      const bp = c.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 2400;
      bp.Q.value = 3;
      src.connect(bp).connect(g);
      envelope(g, t, 0.5, 0.002, 0.025);
      src.start(t, Math.random() * 0.5, 0.05);
      break;
    }
    case 'detent': {
      // a short square blip, like a rotary switch
      const o = c.createOscillator();
      o.type = 'square';
      o.frequency.value = 1150;
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 3000;
      o.connect(lp).connect(g);
      envelope(g, t, 0.18, 0.002, 0.035);
      o.start(t);
      o.stop(t + 0.06);
      break;
    }
    case 'swell': {
      // two detuned saws through an opening low-pass: a deep swell over ~3 s
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(180, t);
      lp.frequency.exponentialRampToValueAtTime(1100, t + 2.2);
      lp.connect(g);
      for (const f of [55, 55.6, 82.4]) {
        const o = c.createOscillator();
        o.type = 'sawtooth';
        o.frequency.value = f;
        o.connect(lp);
        o.start(t);
        o.stop(t + 3.4);
      }
      envelope(g, t, 0.22, 1.6, 1.7);
      break;
    }
    case 'ping': {
      // sonar-like ping with a falling overtone
      const o = c.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(880, t);
      o.frequency.exponentialRampToValueAtTime(660, t + 0.9);
      o.connect(g);
      envelope(g, t, 0.35, 0.005, 1.4);
      o.start(t);
      o.stop(t + 1.5);
      break;
    }
    case 'rumble': {
      // low filtered noise rising and fading with the intro sweep
      const src = c.createBufferSource();
      src.buffer = noiseBuffer(c);
      src.loop = true;
      const lp = c.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(90, t);
      lp.frequency.linearRampToValueAtTime(260, t + 2.5);
      src.connect(lp).connect(g);
      envelope(g, t, 0.6, 1.8, 3.2);
      src.start(t);
      src.stop(t + 5.2);
      break;
    }
  }
}

/** Haptic pulse on closest approach (Android browsers; a no-op elsewhere). Only with sound on and full motion. */
export function haptic(): void {
  if (!get(soundOn) || get(motionReduced)) return;
  try {
    globalThis.navigator?.vibrate?.([25, 40, 60]);
  } catch {
    // not supported
  }
}

/** Clicks for every HUD button and the throttle (installed once by the HUD). */
export function installClickSounds(root: Document = document): () => void {
  const onClick = (e: Event): void => {
    const el = e.target as Element | null;
    if (el?.closest?.('button')) cue('click');
  };
  root.addEventListener('click', onClick, true);
  return () => root.removeEventListener('click', onClick, true);
}
